import React, { useState } from "react";
import EditGroupModal from "./EditGroupModal";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import linkifyHtml from "linkify-html";

export default function GroupDetailsSidebar({
  group,
  activeFilter,
  onAddMemberClick,
  onRemoveMember,
  onMemberProfileClick,
  selectedGroup,
  currentUserId,
  onGroupDeleted, // Add this prop to handle group deletion callback
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Add state for group delete modal
  const [showGroupDeleteModal, setShowGroupDeleteModal] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  // Early return if group is not available
  if (!group || !group.about) {
    return null;
  }

  // for see more in about section
  const aboutText = group.about || "";
  const words = aboutText.trim().split(/\s+/);
  const shouldTruncate = words.length > 30;
  const displayText =
    !expanded && shouldTruncate
      ? words.slice(0, 80).join(" ") + "..."
      : aboutText;

  const linkifiedGroupDescription = linkifyHtml(displayText, {
    target: "_blank",
    rel: "noopener noreferrer",
    attributes: {
      style: "color: primary;",
    },
  });

  // Check if current user is admin of this group
  const isCurrentUserAdmin = group.members?.some(
    (member) => member.role === "Admin" && member.user.id === currentUserId,
  );

  const handleRemoveClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmRemoveMember = async (shouldRemove) => {
    if (!shouldRemove) {
      setShowDeleteModal(false);
      return;
    }

    try {
      setIsRemoving(true);
      await onRemoveMember(memberToDelete.user.id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Failed to remove member:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  // Add group delete handler
  const handleDeleteGroupClick = () => {
    setShowGroupDeleteModal(true);
  };

  const confirmDeleteGroup = async (shouldDelete) => {
    if (!shouldDelete) {
      setShowGroupDeleteModal(false);
      return;
    }

    try {
      setIsDeletingGroup(true);
      await api.delete(`user/tribe-group/${group.id}`);
      setShowGroupDeleteModal(false);
      // Call callback to handle group deletion (e.g., redirect or refresh)
      toast.success("Group Deleted successfully!");
      if (onGroupDeleted) {
        onGroupDeleted();
      }
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error("Failed to delete group!");
    } finally {
      setIsDeletingGroup(false);
    }
  };

  return (
    <div
      className="squads-right-bar"
      style={{ maxWidth: "310px", width: "100%" }}
    >
      {/* About Section */}
      <div className="d-flex justify-content-between mt-1">
        <span className="text-body-b1">About</span>
        {isCurrentUserAdmin && (
          <div>
            <button
              className="text-body-b3 squads-edits"
              data-bs-toggle="modal"
              data-bs-target="#editGroupModal"
              style={{
                background: "none",
                border: "none",
                color: "#008CFF",
                cursor: "pointer",
                padding: 0,
              }}
            >
              Edit
            </button>
            <button
              className="text-body-b3 squads-edits"
              onClick={handleDeleteGroupClick}
              style={{
                background: "none",
                border: "none",
                color: "#a02b07ff",
                cursor: "pointer",
                padding: 0,
                marginLeft: "10px",
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="d-flex gap-2 flex-column">
        <span className="squads-message-title">Group Details</span>
        <span
          className="text-body-b4 message-title-content-squads"
          dangerouslySetInnerHTML={{
            __html: linkifiedGroupDescription || "No description available.",
          }}
          style={{
            wordWrap: "break-word",
          }}
        ></span>
        {shouldTruncate && (
          <button
            className="text-body-b3 squads-edits"
            style={{
              background: "none",
              border: "none",
              color: "#008CFF",
              cursor: "pointer",
              padding: 0,
              textAlign: "left",
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "See Less" : "See More"}
          </button>
        )}
      </div>

      <hr
        className="my-3 my-md-4"
        style={{
          borderTop: "1px solid var(--Bg-Border, #1b2c3a)",
          opacity: 1,
        }}
      />

      {/* Members Section */}
      <div
        className="no-scrollbar flex-grow-1"
        style={{ height: "0px", minHeight: "0px" }}
      >
        <div className="d-flex gap-2 flex-column">
          <div className="d-flex align-items-center">
            <span className="text-body-b1">Members</span>
          </div>
          <span className="text-body-b4">
            Total Members ({group.members?.length || 0})
          </span>
          {isCurrentUserAdmin && (
            <button
              className="text-body-b3"
              style={{
                color: "#008cff",
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
                textAlign: "left",
              }}
              data-bs-toggle="modal"
              data-bs-target="#addMemberGroupModal"
              onClick={onAddMemberClick}
            >
              <span>+</span> Add Member
            </button>
          )}
        </div>

        {group.members?.map((member, idx) => {
          const canOpenMemberProfile = Boolean(
            onMemberProfileClick && member.user?.id,
          );

          return (
            <div className="chat-items" key={idx}>
              <div className="d-flex justify-content-between align-items-center mt-3 mb-3 gap-2">
                <button
                  type="button"
                  className="d-flex align-items-center"
                  onClick={() => onMemberProfileClick?.(member)}
                  disabled={!canOpenMemberProfile}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    cursor: canOpenMemberProfile ? "pointer" : "default",
                    opacity: 1,
                    padding: 0,
                    textAlign: "left",
                  }}
                >
                  <img
                    src={member.user?.image || "icons/chat-liam.svg"}
                    className="profile-img"
                    alt={member.user?.full_name || "User"}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                  <div className="ms-3">
                    <div style={{ fontWeight: 500 }}>
                      {member.user?.full_name}
                    </div>
                    <div className="text-small-s2" style={{ color: "#7E8897" }}>
                      @{member.user?.slug}
                    </div>
                  </div>
                </button>
                {isCurrentUserAdmin &&
                  activeFilter === "createdByYou" &&
                  member.role !== "Admin" && (
                    <button
                      className="text-small-s2"
                      style={{
                        color: "#EB5757",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                      }}
                      onClick={() => handleRemoveClick(member)}
                      disabled={isRemoving}
                    >
                      Remove
                    </button>
                  )}
                {member.role === "Admin" && (
                  <span className="text-small-s2" style={{ color: "#C8CED3" }}>
                    Admin
                  </span>
                )}
                {member.role !== "Admin" && member.user?.is_coach && (
                  <span className="text-small-s2" style={{ color: "#C8CED3" }}>
                    Coach
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Member Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="deleteMemberModal"
          tabIndex="-1"
          aria-labelledby="deleteMemberModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <h1 className="modal-title" id="deleteMemberModalLabel">
                  Remove Member
                </h1>
              </div>
              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-wrap"
                  style={{ fontSize: "19px" }}
                >
                  Are you sure you want to remove{" "}
                  {memberToDelete?.user?.full_name} from the group?
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", height: "auto" }}
                    onClick={() => confirmRemoveMember(true)}
                    disabled={isRemoving}
                  >
                    {isRemoving ? "Removing..." : "Yes"}
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px", height: "auto" }}
                    onClick={() => confirmRemoveMember(false)}
                    disabled={isRemoving}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Delete Confirmation Modal */}
      {showGroupDeleteModal && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="deleteGroupModal"
          tabIndex="-1"
          aria-labelledby="deleteGroupModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <h1 className="modal-title" id="deleteGroupModalLabel">
                  Delete Group
                </h1>
              </div>
              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-wrap"
                  style={{ fontSize: "19px" }}
                >
                  Are you sure you want to delete this group? This action cannot
                  be undone.
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", height: "auto" }}
                    onClick={() => confirmDeleteGroup(true)}
                    disabled={isDeletingGroup}
                  >
                    {isDeletingGroup ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px", height: "auto" }}
                    onClick={() => confirmDeleteGroup(false)}
                    disabled={isDeletingGroup}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal - Only rendered for admins */}
      {isCurrentUserAdmin && (
        <EditGroupModal
          groupId={selectedGroup}
          onGroupUpdated={() => window.location.reload()}
        />
      )}
    </div>
  );
}
